import { createRuntimeEffectRef, getRuntimeEffectRefKey } from '../effects/runtimeLifecycle';

const getEntryEffectId = (entry) => entry?.effectId || entry?.id || null;

const matchesRuntimeEntry = (entry, ref = {}, index = -1) => {
  if (!entry) return false;
  if (ref?.effectId) {
    return getEntryEffectId(entry) === ref.effectId;
  }
  if (ref?.sourceCollection && Number.isInteger(ref?.effectIndex)) {
    return index === ref.effectIndex;
  }
  if (ref?.effectName) {
    const label = typeof entry?.name === 'string' ? entry.name : '';
    return label === ref.effectName || label.includes(ref.effectName) || ref.effectName.includes(label);
  }
  return false;
};

export function buildSpellRuntimeEffectIdentity(entry, sourceCollection, effectIndex) {
  const ref = createRuntimeEffectRef({
    effectId: entry?.effectId || entry?.id || null,
    effectGroupId: entry?.effectGroupId || null,
    sourceCollection,
    effectIndex,
    effectName: entry?.name || null,
    casterId: entry?.casterId || null,
  });

  return {
    effectId: ref.effectId,
    effectGroupId: ref.effectGroupId,
    sourceCollection: ref.sourceCollection,
    effectIndex: ref.effectIndex,
    effectName: ref.effectName,
    casterId: ref.casterId,
  };
}

export function resolveSpellBeneficialRuntimeOwner({
  targetId,
  chars = {},
  pendingChars = {},
}) {
  const targetChar = pendingChars?.[targetId] || chars?.[targetId] || null;
  if (!targetChar) {
    return null;
  }

  return {
    sourceCollection: 'char.activeEffects',
    targetChar: { ...targetChar },
  };
}

const resolveRuntimeAmount = (value) => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  const str = String(value);
  if (str.match(/\d*d\d+/i)) return str;
  return parseInt(str, 10) || 0;
};

const toSaveLifecycleEntry = (entry, effectRef) => ({
  ...entry,
  type: 'save_only',
  tickTiming: entry.saveTiming || entry.tickTiming || 'end_of_target_turn',
  effectRef,
});

export function buildSpellRuntimeLifecycleEntries(entry, sourceCollection, effectIndex) {
  if (!entry) return [];

  const effectRef = createRuntimeEffectRef({
    effectId: entry?.id || entry?.effectId || null,
    effectGroupId: entry?.effectGroupId || null,
    sourceCollection,
    effectIndex,
    effectName: entry?.name || null,
    casterId: entry?.casterId || null,
  });

  const baseEntry = {
    name: entry.name,
    tickTiming: entry.tickTiming || 'start_of_target_turn',
    casterId: entry.casterId,
    saveType: entry.saveType,
    saveDC: entry.saveDC,
    saveTiming: entry.saveTiming,
    saveRemovesEffect: entry.saveRemovesEffect,
    effectId: effectRef.effectId,
    effectGroupId: effectRef.effectGroupId,
    sourceCollection: effectRef.sourceCollection,
    effectIndex: effectRef.effectIndex,
    effectRef,
  };

  const entries = [];
  const dotAmount = resolveRuntimeAmount(entry.dotDamage);
  if (dotAmount) {
    entries.push({
      ...baseEntry,
      type: 'dot',
      amount: dotAmount,
      damageType: entry.dotDamageType || 'fire',
      isDice: typeof dotAmount === 'string',
    });
  }

  const hotAmount = resolveRuntimeAmount(entry.hotHeal);
  if (hotAmount) {
    entries.push({
      ...baseEntry,
      type: 'hot',
      amount: hotAmount,
      isDice: typeof hotAmount === 'string',
    });
  }

  if (entry.hotTempHp > 0) {
    entries.push({
      ...baseEntry,
      type: 'tempHp',
      amount: entry.hotTempHp,
    });
  }

  if (entry.saveType) {
    entries.push(toSaveLifecycleEntry(baseEntry, effectRef));
  }

  return entries;
}

export function buildSpellRuntimeLifecycleMap(entries = []) {
  const byRef = new Map();
  const byName = new Map();

  const register = (map, key, entry) => {
    const current = map.get(key) || { periodic: null, save: null };
    if (entry.type !== 'save_only' && !current.periodic) {
      current.periodic = entry;
    }
    if ((entry.type === 'save_only' || (entry.saveType && entry.saveDC)) && !current.save) {
      current.save = toSaveLifecycleEntry(entry, entry.effectRef || createRuntimeEffectRef({
        effectId: entry.effectId || null,
        effectGroupId: entry.effectGroupId || null,
        sourceCollection: entry.sourceCollection || null,
        effectIndex: Number.isInteger(entry.effectIndex) ? entry.effectIndex : null,
        effectName: entry.name || null,
        casterId: entry.casterId || null,
      }));
    }
    map.set(key, current);
  };

  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const effectRef = entry.effectRef || createRuntimeEffectRef({
      effectId: entry.effectId || null,
      effectGroupId: entry.effectGroupId || null,
      sourceCollection: entry.sourceCollection || null,
      effectIndex: Number.isInteger(entry.effectIndex) ? entry.effectIndex : null,
      effectName: entry.name || null,
      casterId: entry.casterId || null,
    });
    register(byRef, getRuntimeEffectRefKey(effectRef), { ...entry, effectRef });
    if (typeof entry.name === 'string' && entry.name) {
      register(byName, `name:${entry.name}`, { ...entry, effectRef });
    }
  });

  return { byRef, byName };
}

export function extendSpellRuntimeDuration(entries = [], ref = {}, increment = 1) {
  let changed = false;
  const nextEntries = (Array.isArray(entries) ? entries : []).map((entry, index) => {
    if (changed || !matchesRuntimeEntry(entry, ref, index)) return entry;
    changed = true;
    const currentDuration = typeof entry?.duration === 'number' ? entry.duration : 1;
    return {
      ...entry,
      duration: currentDuration + increment,
    };
  });

  return {
    nextEntries,
    changed,
  };
}

export function removeSingleSpellRuntimeCondition(conditions = [], label = '') {
  let removed = false;
  const nextConditions = (Array.isArray(conditions) ? conditions : []).filter((condition) => {
    if (removed) return true;
    const conditionLabel = typeof condition === 'string'
      ? condition
      : (typeof condition?.name === 'string' ? condition.name : '');
    if (!label) return true;
    if (
      conditionLabel === label
      || conditionLabel.includes(label)
      || label.includes(conditionLabel)
    ) {
      removed = true;
      return false;
    }
    return true;
  });

  return {
    nextConditions,
    removed,
  };
}
